<template>
  <aside
    class="n-sidebar"
    :class="{
      'n-sidebar--active': isActive,
      'n-sidebar--left': props.direction === 'left',
      'n-sidebar--right': props.direction === 'right'
    }"
  >
    <div class="n-sidebar__inner">
      <header class="n-sidebar__header" v-if="showHeader">
        <i
          v-if="showLogo && headerData && headerData.logoBlack"
          class="n-sidebar__header__logo"
          :style="`--logo-black: url(${headerData.logoBlack})`"
        />
        <button class="n-sidebar__header__close" @click="onClose" :aria-label="$t('common.close')">
          <x-icon class="n-sidebar__header__close__icon" />
        </button>
      </header>

      <slot name="content" />
    </div>
    <div class="n-sidebar__overlay" @click="onClose" />
  </aside>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, ref } from 'vue';
import { useStoryblokStore } from '@/stores/storyblok';
import { useUiStore } from '@/stores/ui';
import { useNuxtApp } from '#imports';
import { sendButtonClickedEvent } from '@/resources/tracking';

const { $router } = useNuxtApp();

interface Props {
  direction?: 'left' | 'right';
  showLogo?: boolean;
  showHeader?: boolean;
  stateKey: string;
}

const props = withDefaults(defineProps<Props>(), {
  direction: 'left',
  showLogo: false,
  showHeader: true,
  stateKey: ''
});
const emit = defineEmits(['close']);

const XIcon = defineAsyncComponent(() => import('@/assets/icons/x_icon.svg?component'));

const uiStore = useUiStore();
const storyblokStore = useStoryblokStore();
const headerData = computed(() => storyblokStore.globalComponents?.header ?? null);

const isActive = computed(() => uiStore.state[props.stateKey]);

function onClose() {
  uiStore.toggleState(props.stateKey);
  uiStore.closeCartSidebar();

  const newPath = $router.currentRoute?.value?.fullPath?.split('#')[0];
  $router.replace(newPath);

  const baseURL = window?.location?.origin;

  sendButtonClickedEvent({
    pagePath: baseURL + $router.currentRoute.value.fullPath,
    pageUrl: baseURL + $router.currentRoute.value.fullPath,
    pageTitle: document?.title,
    targetUrl: baseURL + $router.currentRoute.value.fullPath,
    label: 'Close',
    buttonAction: 'close sheet'
  });

  emit('close');
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_position.scss' as *;

.n-sidebar {
  @include fixed(0, 0, 0, 0);
  width: 100vw;
  z-index: 1000;
  opacity: 0;
  transition: opacity 100ms ease;
  z-index: 1;
  visibility: hidden;
  &__inner {
    background: white;
    max-width: 500px;
    width: 100%;
    height: 100%;
    filter: drop-shadow(4px 6px 8px rgba(0, 0, 0, 0.3));
    overflow-x: hidden;
    overflow-y: auto;
    transform: translateX(-100%);
    transition: transform 300ms ease;

    .n-sidebar--right & {
      transform: translateX(100%);
    }

    .n-sidebar--active.n-sidebar--left & {
      transform: translateX(0%);
    }

    .n-sidebar--active.n-sidebar--right & {
      transform: translateX(0%);
      position: absolute;
      right: 0;
    }
  }

  &__overlay {
    background: rgba(black, 0.2);
    z-index: -1;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    display: none;
  }
  &--active {
    opacity: 1;
    z-index: 20;
    visibility: visible;

    .n-sidebar__overlay {
      display: block;
    }
  }

  &__header {
    padding: 24px 20px 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__logo {
      background: var(--logo-black) no-repeat;
      background-size: 100%;
      width: 100%;
      height: 33px;
      max-width: 118px;
      margin: 5px;
    }

    &__close {
      display: flex;
      align-items: center;
      border: none;
      background: transparent;
      transition: transform 300ms ease;
      transition-delay: 300ms;
      transform: scale(0);

      .n-sidebar--active & {
        transform: scale(1);
      }

      &__icon {
        width: 20px;
      }
    }
  }
}
</style>
